import isUndefined from 'lodash-es/isUndefined';
import { identity, Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { DECORATION_ATTRIBUTE_NAME, isDecorationNode } from '../../helpers/decoration';
import { codeBlockCursorByDeletingContents } from '../../helpers/dom/codeBlock';
import {
  EDITOR_DATA_ATTRIBUTE_NAMES,
  isBlockLevelContent,
  isPhrasingContent,
  removeEditorDataAttributes,
} from '../../helpers/dom/content';
import {
  normalizeLists,
  unwrapPhrasingContentContainers,
  wrapContentForParent,
} from '../../helpers/dom/contentMutations';
import { getElementContents } from '../../helpers/dom/element';
import { isListItemElement } from '../../helpers/dom/list';
import {
  findAncestor,
  isElement,
  isText,
  trimWhitespaceTextFromEndOfNode,
  trimWhitespaceTextFromNode,
  trimWhitespaceTextFromStartOfNode,
} from '../../helpers/dom/node';
import {
  isBreakElement,
  isParagraphElement,
  maybeCollapseParagraphWithPreviousSibling,
} from '../../helpers/dom/paragraph';
import {
  createPointAtEndOfNode,
  createPointAtEndOfNodeContents,
  createPointWithEndOfRange,
  createPointWithOffsetInContainer,
  createRangeWithPoint,
  findLastTextPoint,
  Point,
  PointAffinity,
} from '../../helpers/dom/point';
import {
  emptyCells,
  getSelectedCellsPlan,
  isTableCellElement,
  isTableElement,
} from '../../helpers/dom/table';
import { computeIsElementEmpty } from '../../helpers/elementHtmlEditor/empty';
import { isKarmaTestEnv } from '../../helpers/env';
import { htmlToText } from '../../helpers/htmlToText';
import { createInsertionPoint, INSERTION_POINT_FILLER } from '../../helpers/insertionPoint';
import { everyInIterable, findInIterable, indexOf } from '../../helpers/iterable';
import { nodeIsLink } from '../../helpers/link';
import { sanitizeHtml, SanitizeHtmlOptions } from '../../helpers/sanitizeHtml';
import { collapsedRangeByDeletingContents } from '../../helpers/selection';
import { simplifyStyleInRange } from '../../helpers/style/simplifyStyleInRange';
import { textToHtml } from '../../helpers/textToHtml';
import { SelectionError } from '../../selectionError';
import { createConnectingService } from '../base/connectingService';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { HtmlEditService, NULL_HTML_EDIT_SERVICE } from './htmlEditService';
import { getTableToPaste, pasteCells } from './table/pasteCells';

export type ElementHtmlEditServiceOptions = {
  sanitizeHtmlOptions?: SanitizeHtmlOptions;
  simplifyStylesMaxHtmlLength?: number;
  /** Pre-process HTML before it is inserted in the DOM. */
  preProcessInsertedHtml?: (dirtyHtml: string, document?: Document) => string;
  /** Post-process HTML after standard processing (style inlining, etc.) and before it is inserted in the DOM. */
  postProcessInsertedHtml?: (cleanHtml: string) => string;
  hasTableService?: boolean;
  hasNewDeleteSelection?: boolean;
  /** Whether to keep pre-formatted text (usually code blocks) intact, or convert it to normal lines/whitespace (default). */
  shouldKeepPreformattedText?: boolean;
  shouldPasteAsTextInCodeBlocks?: boolean;
};

export function createElementHtmlEditService(
  element$: Observable<HTMLElement | null>,
  selectionService: ElementSelectionService,
  options: ElementHtmlEditServiceOptions = {},
): HtmlEditService {
  return createConnectingService(
    element$,
    () => NULL_HTML_EDIT_SERVICE,
    element => new ElementHtmlEditService(element, selectionService, options),
  );
}

export class ElementHtmlEditService extends ElementService<HTMLElement> implements HtmlEditService {
  constructor(
    element: HTMLElement,
    selectionService: ElementSelectionService,
    options: ElementHtmlEditServiceOptions = {},
  ) {
    const {
      sanitizeHtmlOptions,
      preProcessInsertedHtml,
      postProcessInsertedHtml,
      hasTableService,
      hasNewDeleteSelection,
      shouldKeepPreformattedText,
      shouldPasteAsTextInCodeBlocks,
    } = options;
    const originalAddAllowedAttributes =
      sanitizeHtmlOptions && sanitizeHtmlOptions.addAllowedAttributes
        ? sanitizeHtmlOptions.addAllowedAttributes
        : [];
    const sanitizeHtmlOptionsExtended = {
      ...sanitizeHtmlOptions,
      addAllowedAttributes: [...originalAddAllowedAttributes, ...EDITOR_DATA_ATTRIBUTE_NAMES],
    };

    super(element);

    this.selectionService = selectionService;
    this.sanitizeHtml = dirtyHtml => sanitizeHtml(dirtyHtml, sanitizeHtmlOptionsExtended);
    this.preProcessInsertedHtml = preProcessInsertedHtml || identity;
    this.postProcessInsertedHtml = postProcessInsertedHtml || identity;
    this.simplifyStylesMaxHtmlLength = options.simplifyStylesMaxHtmlLength || Infinity;
    this.hasTableService = hasTableService || false;
    this.hasNewDeleteSelection = hasNewDeleteSelection || false;
    this.shouldKeepPreformattedText = shouldKeepPreformattedText ?? false;
    this.shouldPasteAsTextInCodeBlocks = shouldPasteAsTextInCodeBlocks || false;
  }

  private readonly selectionService: ElementSelectionService;
  private readonly sanitizeHtml: (dirtyHtml: string) => string;
  private readonly preProcessInsertedHtml: (dirtyHtml: string, document?: Document) => string;
  private readonly postProcessInsertedHtml: (cleanHtml: string) => string;
  private readonly simplifyStylesMaxHtmlLength: number;
  private readonly hasTableService: boolean;
  private readonly hasNewDeleteSelection: boolean;
  private readonly shouldKeepPreformattedText: boolean;
  private readonly shouldPasteAsTextInCodeBlocks: boolean;

  insertText(text: string) {
    const range = this.selectionService.selection?.range;

    // If we are in a code block, insert the text directly.
    const codeBlockCursor = range && codeBlockCursorByDeletingContents(this.element, range);
    if (codeBlockCursor) {
      const { textNode, cursorOffset } = codeBlockCursor;
      const textBeforeCursor = textNode.data.substring(0, cursorOffset);
      const textAfterCursor = textNode.data.substring(cursorOffset);
      textNode.data = textBeforeCursor + text + textAfterCursor;
      this.selectionService.moveToOffsetInContainer(textNode, textBeforeCursor.length + text.length);
      return;
    }

    // Otherwise, convert the text to HTML.
    this.insertCleanHtml(textToHtml(text));
  }

  insertDirtyHtml(dirtyHtml: string) {
    this.insertCleanHtml(
      this.postProcessInsertedHtml(this.sanitizeHtml(this.preProcessInsertedHtml(dirtyHtml, this.document))),
    );
  }

  insertCleanHtml(html: string) {
    const { element, document } = this;

    // Determine the insertion point.
    let insertPoint: Point;

    const { selection } = this.selectionService;
    if (selection) {
      let { range } = selection;
      if (this.shouldPasteAsTextInCodeBlocks) {
        const codeBlockCursor = range && codeBlockCursorByDeletingContents(this.element, range);
        if (codeBlockCursor) {
          const cleanHtmlToText = htmlToText(html);
          this.insertText(cleanHtmlToText);
          return;
        }
      }

      // Check that FF is on.
      if (this.hasTableService) {
        // If clipboard contains only table cells, and cursor is in a table cell, do a pasteCells.
        const tableAncestor = findAncestor(range.commonAncestorContainer, isTableElement, element);
        const cellAncestor = findAncestor(range.startContainer, isTableCellElement, element);
        const tableToPaste = getTableToPaste(html, document);
        if (tableAncestor !== null && cellAncestor !== null && tableToPaste !== null) {
          pasteCells(tableAncestor, cellAncestor, tableToPaste);
          return;
        }
      }

      const didCollapse = !range.collapsed;
      range = collapsedRangeByDeletingContents(range);

      // Set the insertion point using the collapsed range.
      insertPoint = createPointWithEndOfRange(range);

      // If collapsing caused adjacent non-phrasing content elements to split, meld the parts together.
      if (didCollapse && isElement(range.startContainer) && range.startOffset > 0) {
        const nodeBeforeCursor = range.startContainer.childNodes[range.startOffset - 1];
        const nodeAfterCursor = range.startContainer.childNodes[range.startOffset];
        if (
          isElement(nodeBeforeCursor) &&
          isElement(nodeAfterCursor) &&
          nodeBeforeCursor.tagName === nodeAfterCursor.tagName &&
          isBlockLevelContent(nodeBeforeCursor) &&
          nodeAfterCursor.parentNode
        ) {
          insertPoint = createPointAtEndOfNodeContents(nodeBeforeCursor);

          nodeBeforeCursor.appendChild(getElementContents(nodeAfterCursor));
          nodeAfterCursor.parentNode.removeChild(nodeAfterCursor);
        }
      }
    } else {
      // Default to inserting at the end of the element.
      insertPoint = createPointAtEndOfNodeContents(element);
    }

    // Parse the HTML in the context of the insertion point.
    const fragment = createRangeWithPoint(document, insertPoint).createContextualFragment(html);
    trimWhitespaceTextFromNode(fragment);
    unwrapPhrasingContentContainers(fragment, this.shouldKeepPreformattedText);
    normalizeLists(fragment);

    // Nothing left to do if the fragment is empty.
    let { firstChild, lastChild } = fragment;
    if (!firstChild || !lastChild) {
      return;
    }

    // Handle insertions into a non-phrasing content container.
    const insertContainer = findAncestor(
      createRangeWithPoint(document, insertPoint).commonAncestorContainer,
      (node => node !== element && isElement(node) && isBlockLevelContent(node)) as typeof isElement,
      element,
    );

    if (insertContainer) {
      // If this is an empty paragraph or list item, remove the filler.
      if (
        (isParagraphElement(insertContainer) || isListItemElement(insertContainer)) &&
        insertContainer.childNodes.length === 1 &&
        isBreakElement(insertContainer.firstChild)
      ) {
        insertContainer.removeChild(insertContainer.firstChild);
        insertPoint = createPointWithOffsetInContainer(insertContainer, 0);
      }

      // Take phrasing content from the start of the fragment or from the first similar non-phrasing content
      // element in the fragment.
      if (isPhrasingContent(fragment.firstChild)) {
        while (fragment.firstChild && isPhrasingContent(fragment.firstChild)) {
          insertPoint = insertPoint.insertNode(fragment.firstChild, PointAffinity.END);
        }
      } else if (
        isElement(fragment.firstChild) &&
        isCompatibleParent(fragment.firstChild, insertContainer) &&
        fragment.firstChild.firstChild &&
        !fragment.firstChild.hasAttribute('style') &&
        everyInIterable(
          fragment.firstChild.childNodes,
          childNode => isPhrasingContent(childNode) && !isBreakElement(childNode),
        )
      ) {
        trimWhitespaceTextFromStartOfNode(fragment.firstChild);

        if (lastChild === fragment.firstChild) {
          lastChild = fragment.firstChild.lastChild;
        }
        firstChild = fragment.firstChild.firstChild;

        insertPoint = insertPoint.insertNode(getElementContents(fragment.firstChild), PointAffinity.END);
        fragment.removeChild(fragment.firstChild);
      }

      // If the fragment still has content, we'll need to split the container.
      const shouldSplitContainer = fragment.childNodes.length > 0;

      // Take phrasing content off the end of the fragment or from the last similar non-phrasing content
      // element in the fragment.
      if (isPhrasingContent(fragment.lastChild)) {
        while (fragment.lastChild && isPhrasingContent(fragment.lastChild)) {
          insertPoint = insertPoint.insertNode(fragment.lastChild, PointAffinity.START);
        }
      } else if (
        isElement(fragment.lastChild) &&
        isCompatibleParent(fragment.lastChild, insertContainer) &&
        fragment.lastChild.lastChild &&
        !fragment.lastChild.hasAttribute('style') &&
        everyInIterable(
          fragment.lastChild.childNodes,
          childNode => isPhrasingContent(childNode) && !isBreakElement(childNode),
        )
      ) {
        trimWhitespaceTextFromEndOfNode(fragment.lastChild);

        if (lastChild === fragment.lastChild) {
          lastChild = fragment.lastChild.lastChild;
        }

        insertPoint = insertPoint.insertNode(getElementContents(fragment.lastChild), PointAffinity.START);
        fragment.removeChild(fragment.lastChild);
      }

      if (shouldSplitContainer) {
        if (
          isParagraphElement(insertContainer) &&
          insertContainer.childNodes.length === 0 &&
          insertContainer.parentNode
        ) {
          // Remove the empty paragraph, no point in splitting it because there is still content to be inserted but it
          // doesn't go into a paragraph.
          const { parentNode } = insertContainer;
          const childIndex = indexOf(parentNode.childNodes, insertContainer);
          if (childIndex !== -1) {
            parentNode.removeChild(insertContainer);
            insertPoint = createPointWithOffsetInContainer(parentNode, childIndex);
          }
        } else {
          // Split the container at the insertion point.
          insertPoint = insertPoint.hoist(
            {
              condition: () => true,
              splitCondition: node => isText(node) || isPhrasingContent(node) || node === insertContainer,
              splitAffinity: PointAffinity.START,
              startCondition: node => !node.previousSibling,
              endCondition: node => !node.nextSibling,
            },
            element,
          );
        }
      }
    }

    // If there is still content left in the fragment, wrap it appropriately for the target parent.
    const insertParent = createRangeWithPoint(document, insertPoint).commonAncestorContainer;
    wrapContentForParent(insertParent !== element ? insertParent : null, fragment);

    removeEditorDataAttributes(fragment);

    // Insert the remainder of the fragment.
    insertPoint.insertNode(fragment);

    // Determine the end of the pasted content.
    let endOfPastePoint: Point | undefined;
    if (lastChild && lastChild.parentNode) {
      endOfPastePoint = findLastTextPoint(element, lastChild, 10) || createPointAtEndOfNode(lastChild);
    }

    // Simplify styles.
    const shouldSimplifyStyles = html.length <= this.simplifyStylesMaxHtmlLength;

    if (shouldSimplifyStyles && firstChild && firstChild.parentNode && lastChild && lastChild.parentNode) {
      const pastedRange = document.createRange();
      pastedRange.setStartBefore(firstChild);
      pastedRange.setEndAfter(lastChild);

      try {
        const newRange = simplifyStyleInRange(element, pastedRange);

        // Update the end of the pasted content.
        endOfPastePoint = createPointWithEndOfRange(newRange);
      } catch (err) {
        // Ignore selection errors in production, because we don't care about the returned range.
        if (err instanceof SelectionError && !isKarmaTestEnv) {
          // eslint-disable-next-line no-console
          console.error(err);
        } else {
          throw err;
        }
      }
    }

    // Move the cursor to the end of the pasted content.
    if (endOfPastePoint) {
      const shouldScrollIntoView = element.contains(document.activeElement);
      this.selectionService.moveToPoint(endOfPastePoint, shouldScrollIntoView);
    }
  }

  async insertDeferredCleanHtml(callback: () => Promise<string | undefined>) {
    // Insert a placeholder.
    const id = this.insertPlaceholder();
    if (isUndefined(id)) {
      return;
    }

    try {
      const { parentNode: prevParentNode } = this.element;
      const html = await callback();

      // Make sure our element is stil in the DOM and hasn't moved.
      const { parentNode } = this.element;
      if (!parentNode || parentNode !== prevParentNode) {
        return;
      }

      if (isUndefined(html)) {
        this.removePlaceholder(id);
      } else {
        this.replacePlaceholderWithCleanHtml(id, html);
      }
    } catch (err) {
      this.removePlaceholder(id);
      throw err;
    }
  }

  private insertPlaceholder(): string | undefined {
    const { selection } = this.selectionService;
    if (!selection) {
      return undefined;
    }

    const id = uuid();

    const { document } = this;
    const placeholder = document.createElement('span');
    placeholder.setAttribute('editor-placeholder', id);
    placeholder.innerText = '...';

    const { range, isBackward } = selection;
    range.deleteContents();
    range.insertNode(placeholder);
    range.setStartAfter(placeholder);
    this.selectionService.selectWithRange(range, isBackward);

    return id;
  }

  private findPlaceholderElement(id: string): Element | null {
    // Make sure this placeholder ID is safe for querySelector().
    if (!id.match(/^[0-9a-z-]+$/)) {
      return null;
    }

    return this.element.querySelector(`span[editor-placeholder="${id}"]`);
  }

  private removePlaceholder(id: string): void {
    const placeholderElement = this.findPlaceholderElement(id);
    if (!placeholderElement || !placeholderElement.parentNode) {
      return;
    }

    placeholderElement.parentNode.removeChild(placeholderElement);
  }

  private replacePlaceholderWithCleanHtml(id: string, html: string): void {
    const placeholderElement = this.findPlaceholderElement(id);
    if (!placeholderElement) {
      return;
    }

    const range = this.document.createRange();
    range.selectNode(placeholderElement);
    this.selectionService.selectWithRange(range, false);
    this.insertCleanHtml(html);
  }

  deleteSelection() {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range } = selection;
    if (range.collapsed) {
      return;
    }

    if (this.hasTableService) {
      const selectedTableCells = getSelectedCellsPlan(this.element, range); // Will return null if selection contains more than table cells.
      if (selectedTableCells !== null) {
        const focusCell = findAncestor(selection.focusNode, isTableCellElement, this.element);
        emptyCells(selectedTableCells);
        this.selectionService.moveToOffsetInContainer(focusCell || selectedTableCells[0][0], 0);
        return;
      }
    }

    const { commonAncestorContainer, startContainer, startOffset, endContainer } = range;
    const siblings = commonAncestorContainer.childNodes;
    const startSibling = findInIterable(siblings, node => node.contains(startContainer), null);
    const endSibling = findInIterable(siblings, node => node.contains(endContainer), null);

    // Create this point before mutating the DOM, so we benefit from DOM mutation stability.
    let selectPoint = createPointWithOffsetInContainer(startContainer, startOffset);

    range.deleteContents();

    if (this.hasNewDeleteSelection) {
      const insertionPoint = createInsertionPoint(this.document);
      const appendInsertionPoint = (container: Node) => {
        insertionPoint.textContent = INSERTION_POINT_FILLER;
        container.appendChild(insertionPoint);
        selectPoint = createPointWithOffsetInContainer(insertionPoint, 1);
      };

      // We need to check if we're inside a decoration or a link.
      const { parentNode: commonAncestorContainerParentNode } = commonAncestorContainer;
      if (
        commonAncestorContainerParentNode &&
        commonAncestorContainerParentNode.parentNode &&
        (isDecorationNode(commonAncestorContainerParentNode) ||
          nodeIsLink(commonAncestorContainerParentNode)) &&
        commonAncestorContainer.isSameNode(startContainer) &&
        commonAncestorContainer.isSameNode(endContainer)
      ) {
        // We need to check if the decoration we're in is empty.
        const decorationLength = (commonAncestorContainerParentNode.textContent || '').length;
        if (decorationLength === 0) {
          // If the decoration has a next sibling that is not an empty text, replace it with an insertion point.
          const { nextSibling } = commonAncestorContainerParentNode;
          const isNextSiblingEmptyText = nextSibling && isText(nextSibling) && nextSibling.length === 0;
          if (nextSibling && !isNextSiblingEmptyText) {
            insertionPoint.textContent = INSERTION_POINT_FILLER;
            commonAncestorContainerParentNode.parentNode.replaceChild(
              insertionPoint,
              commonAncestorContainerParentNode,
            );
            selectPoint = createPointWithOffsetInContainer(insertionPoint, 1);
          } else {
            // If the decoration has no next sibling, replace it by a <br> to simulate browser behavior.
            const br = this.document.createElement('br');
            commonAncestorContainerParentNode.parentNode.replaceChild(br, commonAncestorContainerParentNode);
            selectPoint = createPointWithOffsetInContainer(br, 0);
          }
        }
      } else {
        if (isElement(commonAncestorContainer))
          // Remove empty decoration nodes and links from commonAncestorContainer.
          for (const node of commonAncestorContainer.querySelectorAll(`a,[${DECORATION_ATTRIBUTE_NAME}]`)) {
            if (node.parentNode && computeIsElementEmpty(this.document, node)) {
              node.parentNode.removeChild(node);
            }
          }

        // Store if a filler was already added.
        let didAddFiller = false;

        const maybeInsertFiller = (container: Node) => {
          /**
           * Return early if:
           *  - container is not an element as we won't be able to append a node to it
           *  - container still has child nodes that are not text or still have textContent
           *  - a filler was already added
           */
          const notEmptyChildNodes = Array.from(container.childNodes).filter(
            node => !isText(node) || (node.textContent && node.textContent.length !== 0),
          );

          if (!isElement(container) || notEmptyChildNodes.length !== 0 || didAddFiller) return;

          if (isPhrasingContent(container)) {
            appendInsertionPoint(container);
          } else {
            const br = this.document.createElement('br');
            container.appendChild(br);
            selectPoint = createPointWithOffsetInContainer(container, 0);
          }
          didAddFiller = true;
        };

        const maybeRemoveEmptyTextContainer = (container: Node) => {
          const { parentNode } = container;
          // Check if the container is still inside commonAncestorContainer.
          // We have to do this because it may have been removed if it was a decoration node.
          if (
            parentNode &&
            commonAncestorContainer.contains(container) &&
            isText(container) &&
            container.data.length === 0
          ) {
            parentNode.removeChild(container);
            maybeInsertFiller(parentNode);
          }
        };

        maybeRemoveEmptyTextContainer(startContainer);
        maybeRemoveEmptyTextContainer(endContainer);

        // If commonAncestorContainer is a text node, we need to use its parent node if it exists.
        // Otherwise, use commonAncestorContainer.
        maybeInsertFiller(
          isText(commonAncestorContainer) && commonAncestorContainerParentNode
            ? commonAncestorContainerParentNode
            : commonAncestorContainer,
        );
      }
    } else if (isText(startContainer) && startContainer.data.length === 0 && startContainer.parentNode) {
      // TODO: Remove this block once hasNewDeleteSelection FF is true for everyone.
      const { parentNode } = startContainer;
      parentNode.removeChild(startContainer);

      // Add a filler if it was the last child node.
      if (parentNode.childNodes.length === 0) {
        if (isPhrasingContent(parentNode)) {
          const insertionPoint = createInsertionPoint(this.document);
          insertionPoint.textContent = INSERTION_POINT_FILLER;
          parentNode.appendChild(insertionPoint);
          selectPoint = createPointWithOffsetInContainer(insertionPoint, 1);
        } else {
          const br = this.document.createElement('br');
          parentNode.appendChild(br);
          selectPoint = createPointWithOffsetInContainer(parentNode, 0);
        }
      }
    }

    // Try to collapse now-adjacent siblings, such as when deleting from the middle of one line to the middle of
    // a subsequent line.
    if (
      startSibling &&
      isParagraphElement(endSibling) &&
      startSibling.parentNode &&
      startSibling.nextSibling === endSibling
    ) {
      maybeCollapseParagraphWithPreviousSibling(endSibling);
    }

    this.selectionService.moveToPoint(selectPoint);
  }
}

function isCompatibleParent(sourceParent: Element, destinationParent: Element) {
  return (
    destinationParent.tagName === sourceParent.tagName ||
    (isListItemElement(destinationParent) && isParagraphElement(sourceParent))
  );
}
